<template>
    <div id="page_space_log_report_list" class="sbp sbp-list">
        <div class="sbp-main-layout">
            <header class="sbp-project-name-bar">
                <span class="sbp-project-name-icon" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="1.65">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M8 13h8M8 17h8" />
                    </svg>
                </span>
                <h1 class="sbp-project-name-title">{{ currentProject.name || '当前项目' }}</h1>
            </header>

            <div class="sbp-panel">
                <div class="sbp-panel-header">
                    <div class="sbp-panel-title">📋 日志报告</div>
                    <div class="sbp-panel-actions">
                        <button type="button" class="sbp-btn sbp-btn-primary sbp-btn-sm" @click="openImportModal('create')">
                            ↑ 导入报告
                        </button>
                    </div>
                </div>
                <div class="sbp-filter-bar">
                    <div class="sbp-search-box">
                        <i class="el-icon-search sbp-search-icon" />
                        <input v-model.trim="searchQuery" type="search" class="sbp-search-input"
                            placeholder="搜索报告名称、报告周期…" aria-label="搜索报告">
                    </div>
                </div>
                <div class="sbp-panel-body">
                    <table v-show="rows.length" class="sbp-data-table">
                        <thead>
                            <tr>
                                <th>报告名称</th>
                                <th>报告周期</th>
                                <th class="sbp-col-status">状态</th>
                                <th>导入 / 更新时间</th>
                                <th class="sbp-col-actions">操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in rows" :key="row.id">
                                <td>
                                    <div class="sbp-cell-title">{{ row.title }}</div>
                                    <div v-if="row.subtitle" class="sbp-cell-sub">{{ row.subtitle }}</div>
                                </td>
                                <td>{{ row.periodText }}</td>
                                <td class="sbp-col-status">
                                    <span v-if="row.status === 'published'" class="sbp-status-tag sbp-published">已发布</span>
                                    <span v-else class="sbp-status-tag sbp-pending">待发布</span>
                                </td>
                                <td>{{ row.updatedAt }}</td>
                                <td class="sbp-col-actions">
                                    <span class="sbp-table-actions">
                                        <template v-if="row.status === 'published'">
                                            <button v-if="hasReportFile(row)" type="button"
                                                class="sbp-btn sbp-btn-primary sbp-btn-sm"
                                                @click="downloadReportFile(row)">查看</button>
                                            <button type="button" class="sbp-btn sbp-btn-default sbp-btn-sm"
                                                @click="openImportModal('edit', row)">编辑</button>
                                            <button type="button" class="sbp-btn sbp-btn-unpublish sbp-btn-sm"
                                                @click="onUnpublish(row)">取消发布</button>
                                            <button v-if="row.canDelete" type="button" class="sbp-btn sbp-btn-default sbp-btn-sm"
                                                @click="onDelete(row)">删除</button>
                                        </template>
                                        <template v-else>
                                            <button v-if="hasReportFile(row)" type="button"
                                                class="sbp-btn sbp-btn-primary sbp-btn-sm"
                                                @click="downloadReportFile(row)">查看</button>
                                            <button type="button" class="sbp-btn sbp-btn-default sbp-btn-sm"
                                                @click="openImportModal('edit', row)">编辑</button>
                                            <button type="button"
                                                class="sbp-btn sbp-btn-success sbp-btn-sm sbp-btn-publish-lit"
                                                @click="openPublishConfirm(row)">发布</button>
                                            <button type="button" class="sbp-btn sbp-btn-default sbp-btn-sm"
                                                @click="onDelete(row)">删除</button>
                                        </template>
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-show="!rows.length && !listLoading" class="sbp-table-empty">
                        <el-empty description="暂无日志报告，请点击「导入报告」上传" />
                    </div>
                    <div v-if="listLoading" v-loading="listLoading" class="sbp-table-loading" />
                </div>
                <div v-show="rows.length" class="sbp-pagination">
                    <span class="sbp-pagination-info">共 {{ pages.total }} 条</span>
                    <pagination :total="pages.total" :page.sync="pages.currentPage" :limit.sync="pages.pageSize"
                        :pageSizes="[10, 20, 30]" @pagination="handlePageChange" />
                </div>
            </div>
        </div>

        <el-dialog :title="importDialogTitle" custom-class="sbp-report-import-dialog" :visible.sync="importDialogVisible"
            width="520px" append-to-body destroy-on-close @closed="resetImportForm">
            <p class="sbp-space-picker-hint">{{ importDialogHint }}</p>
            <el-form class="sbp-report-import-form" label-width="0" @submit.native.prevent>
                <div class="sbp-report-form-group">
                    <label>报告文件 <span v-if="fileRequired" class="sbp-report-required">*</span></label>
                    <div class="sbp-report-upload-wrap" :class="{ 'is-readonly': importFormReadonly }">
                        <upload :key="'report-upload-' + importUploadKey" ref="reportUpload" zone
                            name="reportFile" :text="uploadZoneText" :is-disabled="importFormReadonly"
                            :list="importUploadList" @uploadFile="onReportUploadFile" />
                        <p v-if="!importFormReadonly" class="sbp-report-upload-hint">支持 PDF、Word，单文件不超过 20MB</p>
                    </div>
                </div>
                <div class="sbp-report-form-group">
                    <label for="report-import-name">报告名称 <span class="sbp-report-required">*</span></label>
                    <input id="report-import-name" v-model.trim="formName" type="text" class="sbp-report-form-input"
                        placeholder="如：结对协作阶段总结" :readonly="importFormReadonly" :disabled="importFormReadonly">
                </div>
                <div class="sbp-report-form-group">
                    <label for="report-import-period">报告周期</label>
                    <input id="report-import-period" v-model.trim="formPeriod" type="text" class="sbp-report-form-input"
                        placeholder="如：2026-04-01 ~ 2026-04-30" :readonly="importFormReadonly"
                        :disabled="importFormReadonly">
                </div>
            </el-form>
            <span v-if="!importFormReadonly" slot="footer" class="dialog-footer">
                <el-button @click="importDialogVisible = false">取 消</el-button>
                <el-button type="primary" :loading="importSaving" @click="submitImportForm">{{ importSubmitText }}</el-button>
            </span>
            <span v-else slot="footer" class="dialog-footer">
                <el-button type="primary" @click="importDialogVisible = false">关 闭</el-button>
            </span>
        </el-dialog>

        <el-dialog title="选择发布的空间" :visible.sync="publishDialogVisible" width="520px" append-to-body
            custom-class="sbp-publish-dialog" @close="pendingPublishRow = null">
            <p class="sbp-space-picker-hint">请选择要将该日志报告发布到的结对空间。学生仅在对应空间内可见该日志报告。</p>
            <el-radio-group v-model="selectedPublishSpaceId" v-loading="publishSpacesLoading"
                class="sbp-space-picker-list">
                <div v-for="sp in publishSpaceOptions" :key="sp.spxId" class="sbp-space-picker-row"
                    :class="{ 'is-checked': selectedPublishSpaceId === sp.spxId }"
                    @click="selectedPublishSpaceId = sp.spxId">
                    <el-radio :label="sp.spxId" class="sbp-space-picker-cb" />
                    <div class="sbp-sp-body">
                        <span class="sbp-sp-name">{{ sp.value }}</span>
                        <div class="sbp-sp-meta">{{ sp.meta }}</div>
                    </div>
                </div>
            </el-radio-group>
            <el-empty v-if="!publishSpacesLoading && !publishSpaceOptions.length" description="暂无可发布的结对空间" />
            <span slot="footer" class="dialog-footer">
                <el-button @click="publishDialogVisible = false">取 消</el-button>
                <el-button type="primary" :loading="publishSaving" @click="confirmPublish">确认发布</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import pagination from '@/common/community/pagination.vue';
    import upload from './components/upload-file.vue';
    import {
        mapState
    } from 'vuex';

    function isApiSuccess(res) {
        return res && (res.code === 0 || res.code === 200);
    }

    function formatFileSize(bytes) {
        const n = Number(bytes);
        if (!n || n <= 0) return '';
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
        return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    }

    function fileSubtitle(rec) {
        if (rec.fileDesc) return rec.fileDesc;
        if (rec.fileName) return rec.fileName;
        const ext = rec.fileExt || (rec.fileName && rec.fileName.split('.').pop());
        const size = formatFileSize(rec.fileSize);
        if (ext && size) return `${String(ext).toUpperCase()} · ${size}`;
        if (rec.successCount != null) return `成功导入 ${rec.successCount} 条`;
        return '';
    }

    function showUploadResultMessage(vm, data, defaultSuccessMsg) {
        const failCount = data && data.failCount != null ? Number(data.failCount) : 0;
        const successCount = data && data.successCount != null ? Number(data.successCount) : 0;
        if (failCount > 0) {
            const errPreview = (data.errors || []).slice(0, 3).join('；');
            const suffix = errPreview ? `。${errPreview}` : '';
            vm.$message.warning(`导入完成：成功 ${successCount} 条，失败 ${failCount} 条${suffix}`);
            return;
        }
        vm.$message.success(defaultSuccessMsg);
    }

    export default {
        name: 'SpaceLogReportList',
        components: {
            pagination,
            upload,
        },
        data() {
            return {
                rows: [],
                pages: {
                    currentPage: 1,
                    pageSize: 10,
                    total: 0,
                },
                searchQuery: '',
                selectedPublishSpaceId: '',
                publishSpaceOptions: [],
                publishSpacesLoading: false,
                _searchDebounce: null,
                listLoading: false,
                importDialogVisible: false,
                importFormMode: 'create',
                editingRow: null,
                formName: '',
                formPeriod: '',
                formFileIdentifier: '',
                formFileName: '',
                importUploadList: [],
                importUploadKey: 0,
                importSaving: false,
                publishDialogVisible: false,
                pendingPublishRow: null,
                publishSaving: false,
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            importDialogTitle() {
                if (this.importFormMode === 'view') return '查看日志报告';
                if (this.importFormMode === 'edit') return '编辑日志报告';
                return '导入日志报告';
            },
            importDialogHint() {
                if (this.importFormMode === 'view') return '查看已导入的报告文件与基本信息';
                if (this.importFormMode === 'edit') return '修改报告文件或基本信息后保存';
                return '上传 PDF 或 Word 文件，填写报告名称与周期';
            },
            importFormReadonly() {
                return this.importFormMode === 'view';
            },
            importSubmitText() {
                return this.importFormMode === 'edit' ? '保 存' : '确认导入';
            },
            fileRequired() {
                return this.importFormMode === 'create';
            },
            uploadZoneText() {
                if (this.importFormReadonly) return '已上传报告文件';
                return '点击或拖拽上传';
            },
        },
        watch: {
            searchQuery() {
                if (this._searchDebounce) {
                    clearTimeout(this._searchDebounce);
                }
                this._searchDebounce = setTimeout(() => {
                    this.pages.currentPage = 1;
                    this.fetchList();
                }, 400);
            },
            'currentProject.id'() {
                this.pages.currentPage = 1;
                this.fetchList();
            },
        },
        mounted() {
            this.fetchList();
        },
        beforeDestroy() {
            if (this._searchDebounce) {
                clearTimeout(this._searchDebounce);
            }
        },
        methods: {
            mapPublishSpaceItem(item) {
                const count = item.studentCount != null ? item.studentCount : 0;
                return {
                    spxId: item.spxId,
                    value: item.spxName || '—',
                    meta: `${count} 名学生`,
                };
            },
            async fetchPublishSpaces() {
                const projectId = this.currentProject && this.currentProject.id;
                if (!projectId) {
                    this.publishSpaceOptions = [];
                    return;
                }
                this.publishSpacesLoading = true;
                try {
                    const res = await this.$Api.PrePrepare.publishSpaces({
                        projectId,
                    });
                    if (isApiSuccess(res)) {
                        const list = Array.isArray(res.data) ? res.data : [];
                        this.publishSpaceOptions = list.map((item) => this.mapPublishSpaceItem(item));
                    } else {
                        this.publishSpaceOptions = [];
                        this.$message.error((res && res.msg) || '获取发布空间失败');
                    }
                } catch (e) {
                    this.publishSpaceOptions = [];
                    this.$message.error('获取发布空间失败');
                } finally {
                    this.publishSpacesLoading = false;
                }
            },
            mapListItem(rec) {
                const st = Number(rec.status);
                const status = st === 1 ? 'published' : 'draft';
                const period = (rec.periodText || rec.period || '').trim();
                const recordId = rec.id != null ? rec.id : rec.recordId;
                return {
                    id: String(recordId),
                    title: rec.title || '—',
                    subtitle: fileSubtitle(rec),
                    periodText: period || '—',
                    period,
                    status,
                    updatedAt: rec.updateTime || rec.importTime || rec.createTime || '—',
                    fileUrl: rec.fileUrl || '',
                    fileIdentifier: rec.identifier || rec.fileIdentifier || '',
                    fileName: rec.fileName || '',
                    fileSize: rec.fileSize,
                    fileExt: rec.fileExt,
                    spaceId: rec.spaceId != null ? rec.spaceId : '',
                    spaceName: rec.spaceName || rec.spxName || '',
                    canDelete: status === 'draft' || rec.canDelete === true || rec.canDelete === 1,
                };
            },
            async fetchList() {
                const projectId = this.currentProject && this.currentProject.id;
                if (!projectId) {
                    this.rows = [];
                    this.pages.total = 0;
                    return;
                }
                const params = {
                    projectId,
                    page: this.pages.currentPage,
                    limit: this.pages.pageSize,
                };
                const kw = (this.searchQuery || '').trim();
                if (kw) {
                    params.keyword = kw;
                }
                this.listLoading = true;
                try {
                    const res = await this.$Api.LogReport.list(params);
                    if (isApiSuccess(res)) {
                        const data = res.data || {};
                        const records = data.list || [];
                        this.rows = records.map((r) => this.mapListItem(r));
                        this.pages.total = data.totalCount != null ? data.totalCount : 0;
                    } else {
                        this.$message.error((res && res.msg) || '获取列表失败');
                        this.rows = [];
                        this.pages.total = 0;
                    }
                } catch (e) {
                    this.$message.error('获取列表失败');
                    this.rows = [];
                    this.pages.total = 0;
                } finally {
                    this.listLoading = false;
                }
            },
            handlePageChange({
                pageIndex,
                pageSize
            }) {
                this.pages.currentPage = pageIndex;
                this.pages.pageSize = pageSize;
                this.fetchList();
            },
            hasReportFile(row) {
                return !!(row && (row.fileUrl || row.fileIdentifier));
            },
            getReportFileUrl(row) {
                if (!row) return '';
                if (row.fileUrl) {
                    const url = String(row.fileUrl).trim();
                    if (/^https?:\/\//i.test(url)) {
                        return url;
                    }
                    const base = window.downloadURL || '';
                    return base ? `${base}${url}` : url;
                }
                if (row.fileIdentifier) {
                    return `${window.downloadURL || ''}${row.fileIdentifier}`;
                }
                return '';
            },
            downloadReportFile(row) {
                const url = this.getReportFileUrl(row);
                if (!url) {
                    this.$message.warning('暂无报告文件');
                    return;
                }
                window.open(url);
            },
            openImportModal(mode, row) {
                this.importFormMode = mode;
                this.editingRow = row || null;
                this.formName = '';
                this.formPeriod = '';
                this.formFileIdentifier = '';
                this.formFileName = '';
                this.importUploadList = [];
                this.importUploadKey += 1;

                if (row) {
                    this.formName = row.title === '—' ? '' : row.title;
                    this.formPeriod = row.period || '';
                    this.formFileIdentifier = row.fileIdentifier || '';
                    this.formFileName = row.fileName || '';
                    if (row.fileIdentifier || row.fileUrl) {
                        this.importUploadList = [{
                            identifier: row.fileIdentifier || '',
                            filename: row.fileName || row.subtitle || '报告文件',
                            filemd5: row.fileUrl || (row.fileIdentifier
                                ? `${window.downloadURL || ''}${row.fileIdentifier}`
                                : ''),
                        }];
                    }
                }
                this.importDialogVisible = true;
            },
            resetImportForm() {
                this.importFormMode = 'create';
                this.editingRow = null;
                this.formName = '';
                this.formPeriod = '';
                this.formFileIdentifier = '';
                this.formFileName = '';
                this.importUploadList = [];
                this.importSaving = false;
            },
            onReportUploadFile(data) {
                if (this.importFormReadonly) return;
                const fileList = data.fileList || [];
                const fileMD5List = data.fileMD5List || [];
                if (fileList.length && fileMD5List.length) {
                    this.formFileIdentifier = fileMD5List[0];
                    this.formFileName = fileList[0].name || '';
                    if (!(this.formName || '').trim() && this.formFileName) {
                        this.formName = this.formFileName.replace(/\.(pdf|docx?|PDF|DOCX?)$/i, '');
                    }
                } else if (!fileList.length) {
                    this.formFileIdentifier = '';
                    this.formFileName = '';
                }
            },
            async submitImportForm() {
                const title = (this.formName || '').trim();
                if (!title) {
                    this.$message.warning('请填写报告名称');
                    return;
                }
                const projectId = this.currentProject && this.currentProject.id;
                if (!projectId) {
                    this.$message.warning('未获取到当前项目');
                    return;
                }
                const periodText = (this.formPeriod || '').trim();
                const identifier = this.formFileIdentifier;

                if (this.importFormMode === 'create' && !identifier) {
                    this.$message.warning('请选择要导入的报告文件');
                    return;
                }

                this.importSaving = true;
                try {
                    let res;
                    if (this.importFormMode === 'edit' && this.editingRow) {
                        const updatePayload = {
                            id: Number(this.editingRow.id),
                            title,
                            periodText: periodText || undefined,
                        };
                        if (identifier) {
                            updatePayload.identifier = identifier;
                        }
                        res = await this.$Api.LogReport.update(updatePayload);
                    } else {
                        res = await this.$Api.LogReport.upload({
                            projectId,
                            title,
                            periodText: periodText || undefined,
                            identifier,
                        });
                    }
                    if (isApiSuccess(res)) {
                        if (this.importFormMode === 'edit') {
                            const data = res.data || {};
                            if (data.failCount > 0) {
                                showUploadResultMessage(this, data, '报告已保存');
                            } else {
                                this.$message.success('报告已保存');
                            }
                        } else {
                            showUploadResultMessage(this, res.data || {}, `报告「${title}」已导入`);
                        }
                        this.importDialogVisible = false;
                        this.pages.currentPage = 1;
                        await this.fetchList();
                    } else {
                        this.$message.error((res && res.msg) || '保存失败');
                    }
                } catch (e) {
                    this.$message.error('保存失败');
                } finally {
                    this.importSaving = false;
                }
            },
            async openPublishConfirm(row) {
                if (row.status === 'published') return;
                this.pendingPublishRow = row;
                this.selectedPublishSpaceId = '';
                this.publishDialogVisible = true;
                await this.fetchPublishSpaces();
                if (this.publishSpaceOptions.length && !this.selectedPublishSpaceId) {
                    this.selectedPublishSpaceId = this.publishSpaceOptions[0].spxId;
                }
            },
            getSelectedPublishSpaceName() {
                const sp = this.publishSpaceOptions.find((o) => o.spxId === this.selectedPublishSpaceId);
                return sp ? sp.value : '';
            },
            async confirmPublish() {
                if (!this.pendingPublishRow) return;
                if (this.selectedPublishSpaceId === '' || this.selectedPublishSpaceId == null) {
                    this.$message.warning('请选择要发布到的结对空间');
                    return;
                }
                const spaceName = this.getSelectedPublishSpaceName();
                this.publishSaving = true;
                try {
                    const res = await this.$Api.LogReport.setStatus({
                        id: Number(this.pendingPublishRow.id),
                        status: 1,
                        spaceId: Number(this.selectedPublishSpaceId),
                    });
                    if (isApiSuccess(res)) {
                        this.$message.success(spaceName ? `已发布至：${spaceName}` : '报告已发布');
                        this.publishDialogVisible = false;
                        this.pendingPublishRow = null;
                        await this.fetchList();
                    } else {
                        this.$message.error((res && res.msg) || '发布失败');
                    }
                } catch (e) {
                    this.$message.error('发布失败');
                } finally {
                    this.publishSaving = false;
                }
            },
            onUnpublish(row) {
                if (row.status !== 'published') return;
                this.$confirm('取消发布后，学生在空间内将不再看到该报告。确定取消？', '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning',
                }).then(async () => {
                    try {
                        const res = await this.$Api.LogReport.setStatus({
                            id: Number(row.id),
                            status: 0,
                        });
                        if (isApiSuccess(res)) {
                            this.$message.success('已取消发布，当前为待发布');
                            await this.fetchList();
                        } else {
                            this.$message.error((res && res.msg) || '取消发布失败');
                        }
                    } catch (e) {
                        this.$message.error('取消发布失败');
                    }
                }).catch(() => {});
            },
            onDelete(row) {
                this.$confirm(`确定删除报告「${row.title}」？`, '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning',
                }).then(async () => {
                    try {
                        const res = await this.$Api.LogReport.delete({
                            id: Number(row.id),
                        });
                        if (isApiSuccess(res)) {
                            this.$message.success('报告已删除');
                            await this.fetchList();
                        } else {
                            this.$message.error((res && res.msg) || '删除失败');
                        }
                    } catch (e) {
                        this.$message.error('删除失败');
                    }
                }).catch(() => {});
            },
        },
    };
</script>

<style lang="less" scoped>
    @primary: #409eff;
    @primary-dark: #337ecc;
    @primary-light: #ecf5ff;
    @success: #67c23a;
    @success-bg: #f0f9eb;
    @warning: #e6a23c;
    @warning-bg: #fdf6ec;
    @danger: #f56c6c;
    @info: #909399;
    @info-bg: #f4f4f5;
    @bg: #f5f7fa;
    @text-primary: #303133;
    @text-regular: #606266;
    @text-secondary: #909399;
    @text-placeholder: #c0c4cc;
    @border: #dcdfe6;
    @border-light: #ebeef5;
    @shadow-light: 0 2px 12px rgba(0, 0, 0, 0.08);
    @radius: 8px;

    .sbp {
        font-size: 14px;
        color: @text-primary;
        line-height: 1.5;
    }

    .sbp-main-layout {
        max-width: 1400px;
        margin: 0 auto;
        padding: 16px 20px 40px;
    }

    .sbp-project-name-bar {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
        padding: 2px 0 4px;
    }

    .sbp-project-name-icon {
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: @primary-light;
        color: @primary;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .sbp-project-name-title {
        font-size: 18px;
        font-weight: 700;
        color: @text-primary;
        line-height: 1.45;
        margin: 0;
        letter-spacing: -0.02em;
        flex: 1;
        min-width: 0;
    }

    .sbp-panel {
        background: #fff;
        border-radius: @radius;
        box-shadow: @shadow-light;
        overflow: hidden;
    }

    .sbp-panel-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        border-bottom: 1px solid @border-light;
        flex-wrap: wrap;
        gap: 10px;
    }

    .sbp-panel-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 15px;
        font-weight: 600;
        color: @text-primary;
    }

    .sbp-panel-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .sbp-filter-bar {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 16px;
        background: @bg;
        border-bottom: 1px solid @border-light;
        flex-wrap: wrap;
    }

    .sbp-search-box {
        flex: 1;
        min-width: 200px;
        position: relative;
    }

    .sbp-search-input {
        width: 100%;
        padding: 7px 12px 7px 32px;
        border: 1px solid @border;
        border-radius: 6px;
        font-size: 13px;
        outline: none;
        transition: all 0.25s ease;
        box-sizing: border-box;

        &:focus {
            border-color: @primary;
            box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
        }
    }

    .sbp-search-icon {
        position: absolute;
        left: 10px;
        top: 50%;
        transform: translateY(-50%);
        color: @text-placeholder;
        font-size: 14px;
    }

    .sbp-panel-body {
        padding: 0;
        overflow-x: auto;
        min-height: 200px;
    }

    .sbp-table-empty {
        padding: 48px 16px 64px;
        text-align: center;
    }

    .sbp-table-loading {
        min-height: 120px;
    }

    .sbp-data-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;

        thead th {
            text-align: center;
        }

        th {
            padding: 10px 12px;
            background: @bg;
            font-weight: 600;
            color: @text-regular;
            font-size: 12px;
            white-space: nowrap;
        }

        tbody td {
            padding: 12px;
            border-bottom: 1px solid @border-light;
            vertical-align: middle;
            text-align: center;
        }

        tbody tr:hover td {
            background: @primary-light;
        }
    }

    .sbp-col-actions {
        white-space: nowrap;
    }

    .sbp-cell-title {
        font-weight: 600;
        color: @text-primary;
    }

    .sbp-cell-sub {
        font-size: 12px;
        color: @text-secondary;
        margin-top: 2px;
    }

    .sbp-col-status {
        white-space: nowrap;
        vertical-align: middle;
    }

    .sbp-status-tag {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 100px;
        font-size: 12px;
        font-weight: 500;
        line-height: 1.4;
        box-sizing: border-box;
        white-space: nowrap;
    }

    .sbp-published {
        background: @success-bg;
        color: @success;
    }

    .sbp-pending {
        background: @primary-light;
        color: @primary;
    }

    .sbp-table-actions {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        flex-wrap: nowrap;
        white-space: nowrap;

        .sbp-btn {
            flex-shrink: 0;
        }

        .sbp-btn-sm {
            min-width: 56px;
            justify-content: center;
            box-sizing: border-box;
        }

        .sbp-btn-unpublish {
            min-width: 72px;
        }
    }

    .sbp-btn {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 7px 14px;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.25s ease;
        border: 1px solid transparent;
        font-family: inherit;
    }

    .sbp-btn-sm {
        padding: 5px 10px;
        font-size: 12px;
    }

    .sbp-btn-primary {
        background: @primary;
        color: #fff;
        border-color: @primary;

        &:hover {
            background: @primary-dark;
        }
    }

    .sbp-btn-default {
        background: #fff;
        color: @text-regular;
        border-color: @border;

        &:hover {
            color: @primary;
            border-color: @primary;
            background: @primary-light;
        }
    }

    .sbp-btn-success {
        background: @success;
        color: #fff;
        border-color: @success;

        &:hover {
            background: #529b2e;
            border-color: #529b2e;
        }
    }

    .sbp-btn-publish-lit {
        background: linear-gradient(180deg, #7fd063 0%, @success 100%);
        box-shadow: 0 0 0 1px rgba(103, 194, 58, 0.45), 0 2px 10px rgba(103, 194, 58, 0.35);
        font-weight: 600;

        &:hover {
            filter: brightness(1.02);
        }
    }

    .sbp-btn-unpublish {
        background: #fff;
        color: @warning;
        border-color: @warning;

        &:hover {
            background: @warning-bg;
            color: #b88230;
            border-color: #cf9236;
        }
    }

    .sbp-pagination {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
        padding: 14px 16px;
        border-top: 1px solid @border-light;

        .sbp-pagination-info {
            font-size: 13px;
            color: @text-secondary;
            line-height: 30px;
            white-space: nowrap;
        }

        /deep/ .pagination {
            margin: 0;
            text-align: right;
        }
    }

    .sbp-space-picker-hint {
        font-size: 13px;
        color: @text-secondary;
        margin: 0 0 14px;
        line-height: 1.5;
    }

    .sbp-space-picker-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-height: 320px;
        overflow-y: auto;
    }

    .sbp-space-picker-row {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 12px 14px;
        border: 1px solid @border-light;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.25s ease;
        background: #fff;

        &:hover {
            border-color: @primary;
        }

        &.is-checked {
            border-color: @success;
            background: @success-bg;
        }
    }

    .sbp-space-picker-cb {
        margin-top: 2px;
        flex-shrink: 0;

        /deep/ .el-checkbox__label,
        /deep/ .el-radio__label {
            display: none;
        }
    }

    .sbp-space-picker-list.el-radio-group {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
    }

    .sbp-sp-body {
        flex: 1;
        min-width: 0;
    }

    .sbp-sp-name {
        font-weight: 600;
        font-size: 14px;
        color: @text-primary;
    }

    .sbp-sp-meta {
        font-size: 12px;
        color: @text-secondary;
        margin-top: 4px;
    }

    .sbp-report-form-group {
        margin-bottom: 16px;

        label {
            display: block;
            font-size: 13px;
            font-weight: 600;
            color: @text-primary;
            margin-bottom: 6px;
        }
    }

    .sbp-report-required {
        color: @danger;
    }

    .sbp-report-form-input {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid @border;
        border-radius: 6px;
        font-size: 13px;
        outline: none;
        transition: all 0.25s ease;
        box-sizing: border-box;

        &:focus {
            border-color: @primary;
            box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
        }

        &:disabled,
        &[readonly] {
            background: @bg;
            cursor: default;
        }
    }

    .sbp-report-upload-wrap {
        &.is-readonly {
            pointer-events: none;
            opacity: 0.92;
        }
    }

    .sbp-report-upload-hint {
        margin: 6px 0 0;
        font-size: 12px;
        color: @text-secondary;
        text-align: center;
    }

    .sbp-report-upload-wrap /deep/ .upload-file--zone .upload-zone-trigger {
        padding: 24px 16px;
        font-size: 14px;
        font-weight: 600;
        color: @text-primary;
    }
</style>

<style lang="less">
    .sbp-report-import-dialog {
        .el-dialog__body {
            padding: 16px 20px 8px;
        }
    }
</style>
